import type { CustomFieldDTO } from '@tempo-io/item-store-dto';

interface PublishedCustomFieldValueProps {
  field: CustomFieldDTO;
  value: unknown;
}

const formatValue = (field: CustomFieldDTO, value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  switch (field.type) {
    case 'FIELD_TYPE_TEXT':
    case 'FIELD_TYPE_MULTILINE_TEXT':
    case 'FIELD_TYPE_NOTE':
      return typeof value === 'string' ? value : String(value);

    case 'FIELD_TYPE_NUMERIC':
      return typeof value === 'number' ? value.toString() : String(value);

    case 'FIELD_TYPE_CURRENCY':
      if (typeof value === 'number') {
        const unit = field.currencyUnit || '$';
        return `${unit}${value.toLocaleString()}`;
      }
      return String(value);

    case 'FIELD_TYPE_DATE':
      if (typeof value === 'string') {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) {
          return String(value);
        }
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      }
      return String(value);

    case 'FIELD_TYPE_CHECKBOX':
      return value === true ? 'Yes' : value === false ? 'No' : '—';

    case 'FIELD_TYPE_PICKLIST':
      if (typeof value === 'number') {
        const option = field.picklistOptions.find((opt) => opt.id === value);
        return option?.name || String(value);
      }
      return String(value);

    case 'FIELD_TYPE_MULTI_PICKLIST':
      if (Array.isArray(value)) {
        const names = value
          .map((id) => {
            const option = field.picklistOptions.find((opt) => opt.id === id);
            return option?.name || String(id);
          })
          .filter(Boolean);
        return names.length > 0 ? names.join(', ') : '—';
      }
      return '—';

    case 'FIELD_TYPE_USER':
    case 'FIELD_TYPE_ITEM':
    case 'FIELD_TYPE_LINK':
      return typeof value === 'string' ? value : String(value);

    case 'FIELD_TYPE_MULTI_USER':
    case 'FIELD_TYPE_MULTI_CUSTOMER':
    case 'FIELD_TYPE_MULTI_ITEM':
    case 'FIELD_TYPE_MULTI_LINK':
    case 'FIELD_TYPE_MULTI_TEXT':
      if (Array.isArray(value)) {
        return value.length > 0 ? value.map(String).join(', ') : '—';
      }
      return '—';

    default:
      return String(value);
  }
};

export const PublishedCustomFieldValue = ({ field, value }: PublishedCustomFieldValueProps) => {
  const formattedValue = formatValue(field, value);

  return (
    <div>
      <dt className="text-sm font-medium text-text-neutral-secondary">{field.name}</dt>
      <dd className="text-sm text-text-neutral whitespace-pre-wrap mt-1">{formattedValue}</dd>
    </div>
  );
};
